import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { fetchProductPageData, fetchLandingPagePageData } from '../../store/shop/actions';
import {
  getProductPageData,
  getProductPageLoadingError,
  hasLoadedProductPage,
  isLoadingProductPage,
  hasLoadedLandingPage,
  isLoadingLandingPage,
  getLandingPageLoadingError,
  getRelatedProducts,
  isLoadingAssignments,
  hasLoadedAssignments,
} from '../../store/shop/selectors';
import { isLoggedIn } from '../../store/auth/selectors';
import CLUB_PROP_TYPES from '../../lib/prop-types/club';
import ProductPage from '../../components/shop/product-page';
import PageDataLoadError from '../../components/errors/page-data-load-error';
import AssignmentLoader from '../../components/shop/assignments/loader';
import SeoHead from '../../components/seo';

class ShopProductPageController extends React.Component {
  static async getInitialProps({ isServer, club, query, reduxStore, res, req }) {
    const productId = parseInt(query.id, 10);
    const initialProps = {
      activeSection: 'shop',
      namespacesRequired: ['common', 'shop'],
      pageType: 'shop.product',
      productId,
    };

    if (!isServer) {
      return initialProps;
    }

    try {
      const { error } = await reduxStore.dispatch(fetchProductPageData(club.id, productId, req));
      if (error && error.errorStatusCode && res) {
        res.statusCode = error.errorStatusCode;
      }

      return initialProps;
    } catch (err) {
      return initialProps;
    }
  }

  constructor(props) {
    super(props);
    this.state = {
      isAddingToBasket: false,
    };
  }

  componentDidMount() {
    this.loadIfRequired();
  }

  componentDidUpdate(prevProps) {
    const { productId } = this.props;
    const { productId: prevProductId } = prevProps;
    if (productId !== prevProductId) {
      this.loadIfRequired();
    }
  }

  loadIfRequired = () => {
    const {
      club: { id: clubId },
      hasPreviouslyLoaded,
      isLoading,
      loadingError,
      loadPageData,
      productId,
    } = this.props;
    if (!hasPreviouslyLoaded && !isLoading && !loadingError) {
      loadPageData(clubId, productId);
    }

    this.loadLandingPageIfRequired();
  };

  // The featured products are only available in the landing page payload
  // This is needed until they become available in the product page payload
  loadLandingPageIfRequired = () => {
    const {
      hasLandingPageLoaded,
      isLandingPageLoading,
      landingPageError,
      loadLandingPageData,
      club: { id: clubId },
    } = this.props;
    if (!hasLandingPageLoaded && !isLandingPageLoading && !landingPageError) {
      loadLandingPageData(clubId);
    }
  };

  isLoading = () => {
    const { isLoading, isAssignmentsLoading, hasAssignmentsLoaded, product, userLoggedIn } =
      this.props;

    // Only wait for assignments if they are needed for purchasing
    if (product && product.requiresAssignment && userLoggedIn) {
      return isLoading || !hasAssignmentsLoaded || isAssignmentsLoading;
    }

    return isLoading;
  };

  addToBasket = async (variantId, amount) => {
    const {
      club: { id: clubId },
    } = this.props;
    this.setState({ isAddingToBasket: true });
    const { makeIsomorphicApiRequest } = await import('../../lib/api-proxy');

    const response = await makeIsomorphicApiRequest(`/v2/club-website/${clubId}/basket`, {
      method: 'POST',
      body: JSON.stringify({ variantId, amount }),
    });

    if (response.status === 200) {
      const data = await response.json();
      const { redirect } = data;
      // TODO handle the data not including the redirect property
      window.location = `/token-redirect?url=${encodeURIComponent(redirect)}`;
    } else {
      // TODO handle AddToBasket failure!
      this.setState({ isAddingToBasket: false });
    }
  };

  render() {
    const { loadingError, club } = this.props;

    if (loadingError) {
      return <PageDataLoadError clubId={club.id} {...loadingError} />;
    }

    const {
      product,
      isLoading,
      relatedProducts,
      hasPreviouslyLoaded,
      isLandingPageLoading,
      userLoggedIn,
    } = this.props;
    const { isAddingToBasket } = this.state;

    if (!isLoading && !hasPreviouslyLoaded) {
      return null;
    }

    return (
      <>
        <SeoHead
          titleTranslationKey="shop:product_page_title"
          descriptionTranslationKey="shop:product_page_meta_description"
          route="shop"
          translationValues={{ productName: product.name }}
        />
        <AssignmentLoader clubId={club.id} />
        <ProductPage
          isAddingToBasket={isAddingToBasket}
          club={club}
          addToBasket={this.addToBasket}
          product={product}
          isLoading={this.isLoading()}
          relatedProducts={relatedProducts}
          isLoadingRelatedProducts={isLandingPageLoading}
          isLoggedIn={userLoggedIn}
        />
      </>
    );
  }
}

ShopProductPageController.defaultProps = {
  loadingError: null,
  landingPageError: null,
  product: null,
};

ShopProductPageController.propTypes = {
  club: CLUB_PROP_TYPES.isRequired,
  hasPreviouslyLoaded: PropTypes.bool.isRequired,
  isLoading: PropTypes.bool.isRequired,
  loadPageData: PropTypes.func.isRequired,
  loadingError: PropTypes.shape(),
  productId: PropTypes.number.isRequired,
  product: PropTypes.shape(),
  hasLandingPageLoaded: PropTypes.bool.isRequired,
  isLandingPageLoading: PropTypes.bool.isRequired,
  landingPageError: PropTypes.shape(),
  loadLandingPageData: PropTypes.func.isRequired,
  relatedProducts: PropTypes.arrayOf(PropTypes.shape()).isRequired,
  isAssignmentsLoading: PropTypes.bool.isRequired,
  hasAssignmentsLoaded: PropTypes.bool.isRequired,
  userLoggedIn: PropTypes.bool.isRequired,
};

const mapStateToProps = (state, { productId }) => ({
  loadingError: getProductPageLoadingError(state, productId),
  hasPreviouslyLoaded: hasLoadedProductPage(state, productId),
  isLoading: isLoadingProductPage(state, productId),
  product: getProductPageData(state, productId),
  relatedProducts: getRelatedProducts(state, productId),
  hasLandingPageLoaded: hasLoadedLandingPage(state),
  isLandingPageLoading: isLoadingLandingPage(state),
  landingPageError: getLandingPageLoadingError(state),
  hasAssignmentsLoaded: hasLoadedAssignments(state),
  isAssignmentsLoading: isLoadingAssignments(state),
  userLoggedIn: isLoggedIn(state),
});

const dispatchToProps = {
  loadPageData: fetchProductPageData,
  loadLandingPageData: fetchLandingPagePageData,
};

export default connect(mapStateToProps, dispatchToProps)(ShopProductPageController);
